<!-- Templates -->
<!-- Inventory Worksheet Skeleton -->
<ng-template #inventoryWorksheetSkeleton>
  <app-header [isInventory]="true" [inventoryValue]="'0.00'"> </app-header>
  <ion-content>
    <app-inventory-worksheet-skeleton
      [platform]="platformService?.platformType"
    ></app-inventory-worksheet-skeleton>
    <app-footer></app-footer>
  </ion-content>
</ng-template>

<!-- Desktop/Tablet Header -->
<ng-template
  #desktopTabletHeader
  let-inventory="inventory"
  let-isOnline="isOnline"
  let-validationErrors="validationErrors"
>
  <!-- Breadcrumbs -->
  <ion-row
    *ngIf="(platformService?.isTouchExperience$ | async) === false"
    data-cy="inventory-worksheet-breadcrumbs-row"
    class="inventory-header-crumbs"
  >
    <ion-col data-cy="inventory-worksheet-breadcrumbs-column" size="10">
      <app-breadcrumbs [breadcrumbs]="breadcrumbs"></app-breadcrumbs>
    </ion-col>
    <ng-container
      *ngIf="inventory.numProducts > 0 && inventory?.items?.length > 0"
    >
      <ion-col *ngIf="downloadFlag$ | async" class="right-align" size="2">
        <div
          class="download-button"
          (click)="openInventoryDownloadOptions($event, inventory)"
        >
          <ion-icon
            name="download-outline"
            data-cy="inventory-worksheet-download-icon"
          ></ion-icon>
          {{ 'i18n.common.download' | translate }}
        </div>
      </ion-col>
    </ng-container>
  </ion-row>

  <!-- Inventory Info -->
  <ion-row
    data-cy="inventory-worksheet-header-info-row"
    class="inventory-header-info"
  >
    <!-- Name, Creation Date -->
    <ion-col data-cy="inventory-worksheet-header-name-date-column" size="7">
      <ng-container
        [ngTemplateOutlet]="inventory?.inventoryOptions?.inEditNameMode && inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.edit ? nameEditMode : name"
        [ngTemplateOutletContext]="{ inventory: inventory, validationErrors: validationErrors }"
      ></ng-container>
      <div
        data-cy="inventory-worksheet-header-dates-section"
        class="inventory-dates"
      >
        <div
          data-cy="inventory-worksheet-created-date-text"
          class="inventory-header-date"
        >
          {{ 'i18n.inventory.inventoryWorksheet.created' | translate }}&nbsp;{{
          inventory?.ecomCreatedDtm | date: "M/d/yyyy 'at' h:mm a" }}
        </div>
        <div
          *ngIf="inventory?.status === InventoryStatus.COMPLETED && inventory.completedDtm"
          data-cy="inventory-worksheet-completed-date-text"
          class="inventory-header-date inventory-completed-date"
        >
          {{ 'i18n.inventory.inventoryWorksheet.completed' | translate
          }}&nbsp;{{ inventory?.completedDtm | date: "M/d/yyyy 'at' h:mm a" }}
        </div>
      </div>
      <div
        *ngIf="inventory?.status === InventoryStatus.COMPLETED && inventory.completedDtm"
        data-cy="inventory-worksheet-completed-message-section"
        class="inventory-completed-message"
      >
        <span data-cy="inventory-worksheet-completed-note-text" class="bold">
          {{ 'i18n.common.noteCaps' | translate }}&nbsp;
        </span>
        <span data-cy="inventory-worksheet-completed-message-text">
          {{ 'i18n.inventory.inventoryWorksheet.completedMessage' | translate
          }}&nbsp;{{ inventory?.completedDtm | date: "M/d/yyyy" }}.
        </span>
      </div>
    </ion-col>
    <!-- Total Value, Count/Edit Mode Toggle -->
    <ion-col
      data-cy="inventory-worksheet-header-total-modes-column"
      class="inventory-header-total-and-toggle"
      size="5"
    >
      <div
        data-cy="inventory-worksheet-total-text"
        class="inventory-header-total"
      >
        {{ 'i18n.inventory.inventoryWorksheet.total' | translate }}&nbsp;{{
        (inventory?.totalPrice ? inventory.totalPrice : '0.00') | currency }}
      </div>
      <div class="toggle-ellipsis-container">
        <ng-container
          [ngTemplateOutlet]="toggle"
          [ngTemplateOutletContext]="{ inventory: inventory, isOnline: isOnline }"
        ></ng-container>
        <div
          *ngIf="
            (platformService?.isTouchExperience$ | async) === true &&
            (downloadFlag$ | async) && inventory.numProducts > 0 &&
            inventory?.items?.length > 0"
          data-cy="inventory-banner-ellipsis-section"
        >
          <ion-icon
            data-cy="inventory-banner-ellipsis-icon"
            name="ellipsis-vertical"
            (click)="openInventoryDownloadOptions($event, inventory)"
          ></ion-icon>
        </div>
      </div>
    </ion-col>
  </ion-row>

  <!-- Groups -->
  <ion-row
    data-cy="inventory-worksheet-header-groups-row"
    class="inventory-header-groups"
  >
    <ion-col
      data-cy="inventory-worksheet-header-groups-column"
      class="inventory-header-group-tabs"
      size="12"
    >
      <div
        data-cy="inventory-worksheet-groups-tabs"
        class="inventory-groups-container"
        id="inventory-groups-container"
        [ngClass]="inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.edit ? 'edit-mode' : 'count-mode'"
      >
        <ng-container
          [ngTemplateOutlet]="inventory?.inventoryOptions?.inEditGroupsMode && inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.edit ? groupTabsEditMode : groupTabs"
          [ngTemplateOutletContext]="{ inventory: inventory }"
        ></ng-container>
      </div>
      <div
        *ngIf="inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.edit"
        data-cy="inventory-worksheet-edit-groups-buttons"
        class="inventory-edit-buttons-container"
      >
        <div data-cy="inventory-worksheet-edit-group-button-section">
          <ion-icon
            data-cy="inventory-worksheet-edit-group-button-icon"
            class="inventory-pencil-icon"
            src="../../../../assets/icon/pencil.svg"
            (click)="toggleEditGroupsIcon(inventory?.inventoryOptions?.inEditGroupsMode)"
          ></ion-icon>
        </div>
        <div
          *ngIf="inventory?.inventoryOptions?.inEditGroupsMode"
          data-cy="inventory-worksheet-add-group-button-section"
        >
          <ion-button
            data-cy="inventory-worksheet-add-group-button"
            class="usf-outline-green-button"
            (click)="addGroup(inventory)"
            [disabled]="inventoryWorksheetService.disableAddGroupButton"
          >
            {{ 'i18n.inventory.inventoryWorksheet.addNewGroup' | translate }}
          </ion-button>
        </div>
      </div>
    </ion-col>
  </ion-row>
</ng-template>

<!-- Mobile Header -->
<ng-template
  #mobileHeader
  let-inventory="inventory"
  let-isOnline="isOnline"
  let-validationErrors="validationErrors"
>
  <!-- Count/Edit Mode Toggle -->
  <ion-row
    data-cy="inventory-worksheet-header-toggle-row"
    class="inventory-header-toggle-row"
  >
    <ion-col data-cy="inventory-worksheet-header-toggle-column">
      <div class="toggle-ellipsis-container">
        <ng-container
          [ngTemplateOutlet]="toggle"
          [ngTemplateOutletContext]="{ inventory: inventory, isOnline: isOnline }"
        ></ng-container>
        <ng-container
          *ngIf="inventory.numProducts > 0 && inventory?.items?.length > 0"
        >
          <div
            *ngIf="downloadFlag$ | async"
            data-cy="inventory-banner-ellipsis-section"
          >
            <ion-icon
              data-cy="inventory-banner-ellipsis-icon"
              name="ellipsis-vertical"
              (click)="openInventoryDownloadOptions($event, inventory)"
            ></ion-icon>
          </div>
        </ng-container>
      </div>
    </ion-col>
  </ion-row>

  <!-- Name -->
  <ion-row
    data-cy="inventory-worksheet-header-name-row"
    class="inventory-header-name-row"
    [class.inventory-name-exists]="validationErrors?.nameDateInvalid"
  >
    <ion-col data-cy="inventory-worksheet-header-name-column">
      <ng-container
        [ngTemplateOutlet]="inventory?.inventoryOptions?.inEditNameMode && inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.edit ? editNameInput : name"
        [ngTemplateOutletContext]="{ inventory: inventory, validationErrors: validationErrors }"
      ></ng-container>
    </ion-col>
  </ion-row>

  <!-- Creation Date, Completion Message -->
  <ion-row
    data-cy="inventory-worksheet-header-date-row"
    class="inventory-header-date-row"
  >
    <ion-col data-cy="inventory-worksheet-header-date-column">
      <span data-cy="inventory-worksheet-header-date-text">
        {{ 'i18n.inventory.inventoryWorksheet.created' | translate }}&nbsp;{{
        inventory?.ecomCreatedDtm | date: "M/d/yyyy 'at' h:mm a" }}
      </span>
      <div
        *ngIf="inventory?.status === InventoryStatus.COMPLETED && inventory.completedDtm"
        class="inventory-completed-message"
      >
        <span data-cy="inventory-worksheet-completed-note-text" class="bold">
          {{ 'i18n.common.noteCaps' | translate }}&nbsp;
        </span>
        <span data-cy="inventory-worksheet-completed-message-text">
          {{ 'i18n.inventory.inventoryWorksheet.completedMessage' | translate
          }}&nbsp;{{ inventory?.completedDtm | date: "M/d/yyyy" }}.
        </span>
      </div>
    </ion-col>
  </ion-row>

  <ng-container
    *ngIf="inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.edit"
  >
    <!-- Name Edit Mode Buttons -->
    <div
      *ngIf="inventory?.inventoryOptions?.inEditNameMode"
      data-cy="inventory-worksheet-edit-name-buttons-section"
      class="inventory-edit-name-buttons-container"
    >
      <ng-container
        [ngTemplateOutlet]="editNameButtons"
        [ngTemplateOutletContext]="{ inventory: inventory }"
      >
      </ng-container>
    </div>
    <!-- Group Edit Mode Buttons -->
    <div
      *ngIf="inventory?.inventoryOptions?.inEditGroupsMode"
      class="inventory-add-group-button-container"
      data-cy="inventory-worksheet-edit-group-button-section"
    >
      <ion-button
        data-cy="inventory-worksheet-add-group-button"
        class="usf-outline-green-button"
        (click)="addGroup(inventory)"
      >
        {{ 'i18n.inventory.inventoryWorksheet.addNewGroup' | translate}}
      </ion-button>
    </div>
  </ng-container>

  <!-- Groups -->
  <ion-row
    data-cy="inventory-worksheet-header-groups-row"
    class="inventory-header-groups-row"
  >
    <ion-col
      data-cy="inventory-worksheet-header-groups-column"
      class="inventory-header-group-tabs"
      size="12"
    >
      <div
        class="tabs-container"
        [ngClass]="inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.edit ? 'edit-mode' : 'count-mode'"
      >
        <ng-container
          [ngTemplateOutlet]="inventory?.inventoryOptions?.inEditGroupsMode && inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.edit ? groupTabsEditMode : groupTabs "
          [ngTemplateOutletContext]="{ inventory: inventory }"
        ></ng-container>
      </div>
      <div
        *ngIf="inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.edit"
        data-cy="inventory-worksheet-edit-group-button-section"
      >
        <ion-icon
          data-cy="inventory-worksheet-edit-group-icon"
          class="inventory-pencil-icon"
          src="../../../../assets/icon/pencil.svg"
          (click)="toggleEditGroupsIcon(inventory?.inventoryOptions?.inEditGroupsMode)"
        ></ion-icon>
      </div>
    </ion-col>
  </ion-row>
</ng-template>

<!-- Count/Edit Mode Toggle -->
<ng-template #toggle let-inventory="inventory" let-isOnline="isOnline">
  <ion-segment
    data-cy="inventory-worksheet-count-edit-toggle"
    class="inventory-header-toggle"
    mode="ios"
    value="{{ inventory?.inventoryOptions?.inventoryWorksheetMode }}"
    (ionChange)="updateInventoryWorksheetMode($event)"
    [disabled]="!isOnline"
    [class.offline]="!isOnline"
  >
    <ion-segment-button
      data-cy="inventory-worksheet-count-toggle-button"
      value="{{ InventoryWorksheetMode.count }}"
    >
      <ion-label data-cy="inventory-worksheet-count-toggle-text">
        {{ 'i18n.inventory.inventoryWorksheet.toggleModes.count' | translate }}
      </ion-label>
    </ion-segment-button>
    <ion-segment-button
      data-cy="inventory-worksheet-edit-toggle-button"
      value="{{ InventoryWorksheetMode.edit }}"
    >
      <ion-label data-cy="inventory-worksheet-header-edit-toggle-text">
        {{ 'i18n.inventory.inventoryWorksheet.toggleModes.edit' | translate }}
      </ion-label>
    </ion-segment-button>
  </ion-segment>
</ng-template>

<!-- Inventory Name (NOT editing inventory name) -->
<ng-template #name let-inventory="inventory">
  <div data-cy="inventory-worksheet-name-section" class="inventory-header-name">
    <p data-cy="inventory-worksheet-name-text">
      {{ inventory?.inventoryName }}&nbsp;{{ inventory?.inventoryDate | date:
      'M/d/yyyy' }}
    </p>
    <ion-icon
      *ngIf="inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.edit && inventory?.status !== InventoryStatus.COMPLETED"
      data-cy="inventory-worksheet-edit-name-icon"
      class="inventory-pencil-icon"
      src="../../../../assets/icon/pencil.svg"
      (click)="toggleEditNameIcon(inventory?.inventoryOptions?.inEditNameMode)"
    ></ion-icon>
  </div>
</ng-template>

<!-- Inventory Name (editing inventory name) -->
<ng-template
  #nameEditMode
  let-inventory="inventory"
  let-validationErrors="validationErrors"
>
  <div
    data-cy="inventory-worksheet-edit-name-section"
    class="inventory-header-name"
    [class.inventory-name-exists]="validationErrors?.nameDateInvalid"
  >
    <ng-container
      [ngTemplateOutlet]="editNameInput"
      [ngTemplateOutletContext]="{ inventory: inventory, validationErrors: validationErrors }"
    >
    </ng-container>
    <ng-container
      [ngTemplateOutlet]="editNameButtons"
      [ngTemplateOutletContext]="{ inventory: inventory,  validationErrors: validationErrors  }"
    >
    </ng-container>
  </div>
</ng-template>

<!-- Edit Inventory Name Input -->
<ng-template
  #editNameInput
  let-inventory="inventory"
  let-validationErrors="validationErrors"
>
  <div
    data-cy="inventory-worksheet-edit-name-input-section"
    class="inventory-name-input-section"
    [class.inventory-name-exists]="validationErrors?.nameDateInvalid"
  >
    <ion-input
      data-cy="inventory-worksheet-edit-name-input"
      inputmode="text"
      maxlength="30"
      [value]="inventory?.inventoryName"
      (ionInput)="getInventoryWorksheetNameDateExists($event, inventory.inventoryDate)"
    >
    </ion-input>
    <span
      *ngIf="validationErrors?.nameDateInvalid"
      data-cy="inventory-worksheet-edit-name-error-text"
    >
      {{ 'i18n.inventory.inventoryNameDateError' | translate }}
    </span>
  </div>
</ng-template>

<!-- Edit Inventory Name Buttons -->
<ng-template
  #editNameButtons
  let-inventory="inventory"
  let-validationErrors="validationErrors"
>
  <ion-button
    data-cy="inventory-worksheet-edit-name-save-button"
    class="inventory-save-changes-button"
    [ngClass]="validationErrors?.nameDateInvalid ? 'usf-fill-disabled-button' : 'usf-fill-green-button'"
    (click)="onSaveName()"
  >
    {{ 'i18n.inventory.saveChanges' | translate }}
  </ion-button>
  <span
    data-cy="inventory-worksheet-edit-name-cancel-button"
    class="inventory-cancel-changes-button"
    (click)="toggleEditNameIcon(inventory?.inventoryOptions?.inEditNameMode)"
  >
    {{'i18n.common.cancel' | translate }}
  </span>
</ng-template>

<!-- Groups Tabs (NOT editing group name) -->
<ng-template #groupTabs let-inventory="inventory">
  <div
    id="all-groups-tab"
    data-cy="inventory-worksheet-all-groups-tab"
    (click)="inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.count
            ? updateSelectedGroup({groupName: 'All', numProducts: inventory?.numProducts}, inventory?.inventoryWorksheetCountSort, null)
            : updateSelectedGroup({groupName: 'All', numProducts: inventory?.numProducts}, inventory?.inventoryWorksheetEditSort, null)
          "
    [ngClass]="inventory?.inventoryOptions?.selectedGroup.groupName === 'All' ? 'selected' : 'group'"
    class="all-groups"
  >
    <div data-cy="inventory-worksheet-all-groups-text">
      {{ 'i18n.inventory.inventoryWorksheet.all' | translate }}
    </div>
    <ion-badge
      data-cy="inventory-worksheet-all-groups-icon"
      mode="ios"
      class="group-count"
    >
      {{ inventory?.numProducts }}
    </ion-badge>
  </div>

  <div
    class="other-group-tabs"
    *ngFor="let group of inventory?.groupHeaders; let i = index"
    [id]="'tabs-' + i"
    [ngClass]="group?.groupName === inventory?.inventoryOptions?.selectedGroup.groupName ? 'selected' : 'group'"
    (click)="inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.count
        ? updateSelectedGroup(group, inventory?.inventoryWorksheetCountSort, i)
        : updateSelectedGroup(group, inventory?.inventoryWorksheetEditSort, i)
      "
  >
    <div [attr.data-cy]="'inventory-worksheet-group-text-' + i">
      {{ group?.groupName }}
    </div>
    <ion-badge
      [attr.data-cy]="'inventory-worksheet-group-icon-' + i"
      mode="ios"
      class="group-count"
    >
      {{ group?.numProducts || 0 }}
    </ion-badge>
  </div>
</ng-template>

<!-- Groups Tabs (editing group Name) -->
<ng-template #groupTabsEditMode let-inventory="inventory">
  <!-- groups tabs -->
  <div
    cdkDropList
    (cdkDropListDropped)="onGroupDragDropped(inventory, $event)"
    class="drag-drop-container"
    id="drag-drop-container"
    cdkDropListOrientation="horizontal"
  >
    <!-- all tab -->
    <div class="inventory-edit-mode-all-tab">
      <div data-cy="inventory-worksheet-all-groups-edit-tab" class="group">
        <div data-cy="inventory-worksheet-all-groups-text">
          {{ 'i18n.inventory.inventoryWorksheet.all' | translate }}
        </div>
        <ion-badge
          data-cy="inventory-worksheet-all-groups-icon"
          mode="ios"
          class="inventory-edit-mode-num-products"
        >
          {{ inventory?.numProducts }}
        </ion-badge>
      </div>
    </div>

    <div
      *ngFor="let group of inventory?.groupHeaders; let i = index"
      class="inventory-editable-group"
      [class.redErrorBox]="inventoryWorksheetService.groupNameExists(inventory, group.groupName) || group.groupName.length === 0"
      [attr.data-cy]="'inventory-worksheet-editable-group-' + i"
      cdkDrag
    >
      <ion-icon
        *ngIf="group.groupName !== 'Unassigned Group'"
        class="delete-group-icon"
        name="close-circle-outline"
        [attr.data-cy]="'inventory-worksheet-editable-group-delete-icon-' + i"
        (click)="deleteGroup(inventory, group)"
      ></ion-icon>
      <div
        class="group-name-input"
        [attr.data-cy]="'inventory-worksheet-editable-group-input-section-' + i"
      >
        <ion-input
          [attr.data-cy]="'inventory-worksheet-editable-group-input-' + i"
          maxlength="30"
          size="30"
          [disabled]="group.groupName === 'Unassigned Group'"
          id="group-name-input-{{i}}"
          (ionFocus)="revertGroupName(group)"
          (ionBlur)="updateGroupName(inventory, group)"
          class="group-input"
          [(ngModel)]="group.groupName"
          value="{{group.groupName}}"
          trimSpace
        >
        </ion-input>
        <ion-icon
          [attr.data-cy]="'inventory-worksheet-editable-group-drag-icon-' + i"
          name="menu-outline"
          cdkDragHandle
          slot="end"
        ></ion-icon>
      </div>
      <div *cdkDragPlaceholder>
        <img data-cy="placement-line" src="assets/images/placementline.png" />
      </div>
    </div>
    <div class="shadow-cover">&nbsp;</div>
  </div>
</ng-template>

<!-- Desktop/Tablet Searchbar, Add Products Button, Update Prices Button, Sort Button -->
<ng-template
  #desktopTabletSearchAddUpdateSort
  let-inventory="inventory"
  let-isOnline="isOnline"
>
  <ion-row
    data-cy="inventory-worksheet-search-add-update-sort-row"
    class="inventory-search-add-update-sort-row"
  >
    <ion-col
      data-cy="inventory-worksheet-search-add-update-column"
      size-xl="10.5"
      size-lg="10.5"
      size-md="9"
      size-sm="9"
      size-xs="9"
    >
      <div class="inventory-search-add-update-section">
        <ng-container
          [ngTemplateOutlet]="searchbar"
          [ngTemplateOutletContext]="{ inventory: inventory }"
        ></ng-container>
        <ng-container
          [ngTemplateOutlet]="addProductsButton"
          [ngTemplateOutletContext]="{ isOnline: isOnline}"
        ></ng-container>
        <ng-container
          [ngTemplateOutlet]="updatePricesButton"
          [ngTemplateOutletContext]="{ isOnline: isOnline}"
        ></ng-container>
      </div>
    </ion-col>
    <ion-col
      data-cy="inventory-worksheet-sort-column"
      size-xl="1.5"
      size-lg="1.5"
      size-md="3"
      size-sm="3"
      size-xs="3"
    >
      <ng-container
        *ngIf="(platformService?.isTouchExperience$ | async)"
        [ngTemplateOutlet]="sortButton"
        [ngTemplateOutletContext]="{ inventory: inventory }"
      ></ng-container>
    </ion-col>
  </ion-row>
</ng-template>

<!-- Mobile Searchbar, Add Products Button, Update Prices Button, Sort Button -->
<ng-template
  #mobileSearchAddUpdateSort
  let-inventory="inventory"
  let-isOnline="isOnline"
>
  <ion-row data-cy="inventory-worksheet-search-sort-row" class="inventory-row">
    <ion-col data-cy="inventory-worksheet-search-sort-column" size="12">
      <div class="inventory-search-sort-section">
        <ng-container [ngTemplateOutlet]="searchbar"></ng-container>
        <ng-container
          [ngTemplateOutlet]="sortButton"
          [ngTemplateOutletContext]="{ inventory: inventory }"
        ></ng-container>
      </div>
    </ion-col>
  </ion-row>
  <ion-row
    data-cy="inventory-worksheet-add-update-row"
    class="inventory-row"
    size="12"
  >
    <div class="inventory-add-update-section">
      <ng-container
        [ngTemplateOutlet]="addProductsButton"
        [ngTemplateOutletContext]="{ isOnline: isOnline}"
      ></ng-container>
      <ng-container
        [ngTemplateOutlet]="updatePricesButton"
        [ngTemplateOutletContext]="{ isOnline: isOnline}"
      ></ng-container>
    </div>
  </ion-row>
</ng-template>

<!-- Searchbar -->
<ng-template #searchbar let-inventory="inventory">
  <ion-searchbar
    id="search-bar-{{inventory?.inventoryId}}-0"
    data-cy="inventory-worksheet-searchbar"
    class="search"
    mode="md"
    placeholder="{{
      'i18n.inventory.searchWithinInventoryList' | translate
    }}"
    [(ngModel)]="searchKey"
    (ionInput)="search()"
    (ionClear)="resetSearch()"
    (ionFocus)="resetPreviousHotkeyId()"
    globalkeyhandler="filterBar"
  >
  </ion-searchbar
></ng-template>

<!-- Add Products Button -->
<ng-template #addProductsButton let-isOnline="isOnline">
  <ion-button
    data-cy="inventory-worksheet-add-products-button"
    [class]=" isOnline ? 'usf-fill-green-button' : 'usf-outline-disabled-button'"
    (click)="inventoryWorksheetService.openAddProductsPopover($event)"
    [disabled]="!isOnline"
    [class.offline]="!isOnline"
  >
    <ion-icon
      data-cy="inventory-worksheet-add-products-button-icon"
      name="add-outline"
      slot="start"
      (click)="inventoryWorksheetService.openAddProductsPopover($event)"
    ></ion-icon>
    {{ 'i18n.common.addProducts' | translate }}
  </ion-button>
</ng-template>

<!-- Update Prices Button -->
<ng-template #updatePricesButton let-isOnline="isOnline">
  <ion-button
    data-cy="inventory-worksheet-update-price-button"
    class="usf-outline-green-button"
    (click)="openPriceOptions($event)"
    [class.hide]="!isOnline"
  >
    <ion-icon
      data-cy="inventory-worksheet-update-price-button-icon"
      src="../../../../../../assets/icon/update-icon.svg"
      slot="start"
    ></ion-icon>

    {{ 'i18n.inventory.inventoryWorksheet.pricing.update' | translate }}
  </ion-button>
  <ion-button
    data-cy="inventory-worksheet-update-price-button-offline"
    class="usf-outline-disabled-button offline"
    (click)="openPriceOptions($event)"
    [disabled]="true"
    [class.hide]="isOnline"
  >
    <ion-icon
      data-cy="inventory-worksheet-update-price-button-icon"
      src="../../../../../../assets/icon/update-gray-icon.svg"
      slot="start"
    ></ion-icon>
    {{ 'i18n.inventory.inventoryWorksheet.pricing.update' | translate }}
  </ion-button>
</ng-template>

<!-- Sort Button -->
<ng-template #sortButton let-inventory="inventory">
  <ion-button
    data-cy="inventory-worksheet-sort-button"
    class="usf-outline-green-button sort-button"
    (click)="openOrCloseSortModal(true)"
  >
    <ion-icon
      data-cy="inventory-worksheet-sort-button-icon"
      slot="start"
      name="filter"
    ></ion-icon>
    {{ 'i18n.common.sort' | translate }} {{ isSortApplied ? '(1)' : '' }}
  </ion-button>
</ng-template>

<!-- Selected Products Action Bar -->
<ng-template #selectedProductsActionBar let-inventory="inventory">
  <ion-card
    data-cy="inventory-worksheet-selected-products-bar"
    class="inventory-selected-products-bar"
  >
    <!-- Desktop/Tablet Selected Products Action Bar -->
    <ng-container
      *ngIf="platformService?.platformType !== platformService?.platformEnum?.mobile; else selectedProductsBarMobile"
    >
      <div
        data-cy="inventory-worksheet-product-selection-section"
        class="inventory-product-options"
      >
        <ion-text
          data-cy="inventory-worksheet-num-selected-products-text"
          class="bold-font-weight"
        >
          {{ (inventory?.inventoryOptions?.selections?.length) }}&nbsp;{{
          ((inventory?.inventoryOptions?.selections?.length) > 1 ?
          'i18n.lists.products' : 'i18n.lists.product') | translate }} &nbsp;{{
          'i18n.lists.selected' | translate }}
        </ion-text>
        <ng-container
          *ngIf="inventory?.inventoryOptions?.selections?.length !== inventory?.inventoryOptions?.selectedGroup?.numProducts"
        >
          <div
            class="separator"
            data-cy="inventory-worksheet-separator-image"
            src="assets/images/divider-white.svg"
          ></div>
          <ion-label
            data-cy="inventory-worksheet-select-all-text"
            (click)="selectAllProducts(inventory)"
          >
            {{ 'i18n.lists.selectAll' | translate }}
          </ion-label>
        </ng-container>
        <div
          class="separator"
          data-cy="inventory-worksheet-separator-image"
          src="assets/images/divider-white.svg"
        ></div>
        <ion-label
          data-cy="inventory-worksheet-deselect-all-text"
          (click)="deselectAllProducts()"
        >
          {{ 'i18n.lists.deselectAll' | translate }}
        </ion-label>
      </div>
      <div
        data-cy="inventory-worksheet-product-actions-section"
        class="inventory-product-options"
      >
        <ng-container *ngIf="inventory?.groupHeaders?.length > 1">
          <ion-label
            data-cy="inventory-worksheet-move-products-button"
            class="medium-font-weight"
            (click)="inventoryWorksheetService.openSelectedInventoryItemModal(InventoryActions.moveToGroup, inventory)"
          >
            {{ 'i18n.inventory.inventoryWorksheet.move' | translate }}
          </ion-label>
          <div
            class="separator"
            data-cy="inventory-worksheet-separator-image"
            src="assets/images/divider-white.svg"
          ></div>
          <ion-label
            data-cy="inventory-worksheet-copy-products-button"
            class="medium-font-weight"
            (click)="inventoryWorksheetService.openSelectedInventoryItemModal(InventoryActions.copyToGroup, inventory)"
          >
            {{ 'i18n.inventory.inventoryWorksheet.copy' | translate }}
          </ion-label>
          <div
            class="separator"
            data-cy="inventory-worksheet-separator-image"
            src="assets/images/divider-white.svg"
          ></div>
        </ng-container>
        <ion-label
          *ngIf="!inventoryWorksheetService.allCatchWeight"
          data-cy="inventory-worksheet-change-units-button"
          class="medium-font-weight"
          (click)="inventoryWorksheetService.openSelectedInventoryItemModal(InventoryActions.changeUnits, inventory)"
        >
          {{ 'i18n.inventory.inventoryWorksheet.changeUnits' | translate }}
        </ion-label>
        <div
          *ngIf="!this.inventoryWorksheetService.allCatchWeight"
          class="separator"
          data-cy="inventory-worksheet-separator-image"
          src="assets/images/divider-white.svg"
        ></div>
        <ion-label
          data-cy="inventory-delete-products-button"
          class="bold-font-weight"
          (click)="inventoryWorksheetService.openSelectedInventoryItemDeleteModal(inventory)"
        >
          {{ 'i18n.common.delete' | translate }}
        </ion-label>
      </div>
    </ng-container>
    <!-- Mobile Selected Products Action Bar -->
    <ng-template #selectedProductsBarMobile>
      <div
        data-cy="inventory-worksheet-product-selection-section"
        class="select-deselect-products-mobile"
      >
        <ion-text
          *ngIf="inventory?.inventoryOptions?.selections?.length !==  inventory?.inventoryOptions?.selectedGroup?.numProducts"
          data-cy="inventory-worksheet-select-all-text"
          class="bold-font-weight"
          (click)="selectAllProducts(inventory)"
        >
          {{ 'i18n.lists.selectAll' | translate }}
        </ion-text>
        <span
          data-cy="inventory-worksheet-deselect-all-text"
          (click)="deselectAllProducts()"
        >
          {{ 'i18n.lists.deselectAll' | translate }}
        </span>
      </div>
      <ion-button
        data-cy="inventory-worksheet-selected-products-action-button"
        class="usf-outline-green-button"
        (click)="inventoryWorksheetService.selectedInventoryItemEllipsis(inventory)"
      >
        {{ (inventory?.inventoryOptions?.selections?.length) }}
        {{('i18n.lists.selected') | translate }}
        <ion-icon
          data-cy="inventory-worksheet-select-products-action-ellipsis-icon"
          class="selected-products-ellipsis"
          name="ellipsis-vertical"
        ></ion-icon>
      </ion-button>
    </ng-template>
  </ion-card>
</ng-template>
<!-- End of Templates -->

<ng-container
  *ngIf="inventoryWorksheetService.inventoryService.showSkeletonFlag.value && inventoryWorksheetViewModel$ | async as viewModel else inventoryWorksheetSkeleton"
>
  <app-header
    [isInventory]="true"
    [inventoryValue]="viewModel?.inventory?.totalPrice ? viewModel?.inventory?.totalPrice?.toString() : '0.00'"
  >
  </app-header>

  <ion-content
    *ngIf="viewModel?.isInventoryDataLoaded"
    id="inventory-worksheet"
    class="gray-content-bg"
    scroll-y="false"
    [ngClass]="(platformService?.isTouchExperience$ | async) ? platformService?.platformType === platformService?.platformEnum?.tablet ? 'tablet' : 'mobile' : 'desktop'"
  >
    <cdk-virtual-scroll-viewport
      id="inventory-worksheet-scroll-{{viewModel?.inventory?.inventoryId}}"
      data-cy="inventory-worksheet-scroll"
      class="cdk-scroll inventory-worksheet-scroll"
      orientation="vertical"
      appInvVs
      [itemHeights]="viewModel?.inventory?.itemHeights"
      [indexOffset]="1"
      [bufferBefore]="20"
      [bufferAfter]="20"
      [items]="viewModel?.inventory?.items"
      [isEditMode]="viewModel?.inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.edit"
      [inventoryId]="viewModel?.inventory?.inventoryId"
    >
      <div
        class="inventory-worksheet-header"
        *ngIf="validation$ | async as validationErrors"
      >
        <ion-grid [fixed]="true">
          <ng-container
            *ngIf="platformService?.platformType !== platformService?.platformEnum?.mobile"
            [ngTemplateOutlet]="desktopTabletHeader"
            [ngTemplateOutletContext]="{inventory:viewModel?.inventory, isOnline:viewModel?.isOnline, validationErrors: validationErrors}"
          ></ng-container>
          <ng-container
            *ngIf="platformService?.platformType === platformService?.platformEnum?.mobile"
            [ngTemplateOutlet]="mobileHeader"
            [ngTemplateOutletContext]="{inventory:viewModel?.inventory, isOnline:viewModel?.isOnline, validationErrors: validationErrors}"
          ></ng-container>
        </ion-grid>
      </div>

      <div class="inventory-worksheet-content">
        <ion-grid [fixed]="true">
          <ng-container
            *ngIf="platformService?.platformType !== platformService?.platformEnum?.mobile && viewModel?.inventory?.numProducts !== 0"
            [ngTemplateOutlet]="desktopTabletSearchAddUpdateSort"
            [ngTemplateOutletContext]="{inventory:viewModel?.inventory, isOnline:viewModel?.isOnline}"
          ></ng-container>
          <ng-container
            *ngIf="platformService?.platformType === platformService?.platformEnum?.mobile && viewModel?.inventory?.numProducts !== 0"
            [ngTemplateOutlet]="mobileSearchAddUpdateSort"
            [ngTemplateOutletContext]="{inventory:viewModel?.inventory, isOnline:viewModel?.isOnline}"
          ></ng-container>

          <app-inventory-group-sort-product-data
            [platform]="platformService?.platformType"
            [inventory]="viewModel?.inventory"
            [isOnline]="viewModel?.isOnline"
            (updateSortEvent)="updateInventoryWorksheetSort(viewModel?.inventory?.inventoryOptions?.inventoryWorksheetMode, $event)"
            (selectionEvent)="selectInventoryItem($event)"
            (updateGroupEvent)="moveInventoryItemToNewGroup($event)"
            (updateUOMEvent)="updateInventoryItemUOM($event)"
            (updateQuantityEvent)="updateInventoryItemQuantity($event, viewModel?.inventory)"
            (productDescriptionClick)="openInventoryItemModal($event, viewModel?.inventory)"
            (updatePriceOverrideEvent)="updateInventoryPriceOverride($event)"
            (updateConversionFactorEvent)="updateInventoryConversionFactor($event)"
            (addUSFProductsClick)="inventoryWorksheetService.openAddProductsPopover($event)"
            (toolTipHoverEvent)="showToolTip($event)"
            (toolTipLeaveEvent)="hideToolTip()"
            (itemForInformationEvent)="informationModalOpenRequest($event)"
            (resetPreviousHotkeyIdEvent)="resetPreviousHotkeyId()"
            (lineNumberEvent)="handleLineNumberChange($event, viewModel?.inventory)"
          >
          </app-inventory-group-sort-product-data>
        </ion-grid>
      </div>
      <app-footer></app-footer>
    </cdk-virtual-scroll-viewport>
    <ng-container
      *ngIf="viewModel?.inventory?.inventoryOptions?.selections?.length > 0"
      [ngTemplateOutlet]="selectedProductsActionBar"
      [ngTemplateOutletContext]="{ inventory: viewModel?.inventory }"
    ></ng-container>
  </ion-content>
  <!-- Complete Button -->
  <div
    *ngIf="viewModel?.inventory?.numProducts !== 0 && !(viewModel?.inventory?.status === InventoryStatus.COMPLETED && viewModel?.inventory?.completedDtm)
     && viewModel?.inventory?.inventoryOptions?.selections?.length === 0 && viewModel?.isOnline"
    id="complete-button"
    data-cy="inventory-worksheet-completion-button"
    [ngClass]="(platformService?.isTouchExperience$ | async) ? 'touch' : 'desktop'"
  >
    <ion-button
      data-cy="inventory-worksheet-completion-button"
      [ngClass]="
      platformService.platformType !== platformService.platformEnum.mobile
        ? inventoryWorksheetService.disableCompleteButton
          ? 'usf-fill-disabled-dsktp-tblt-modal-button'
          : 'usf-fill-green-dsktp-tblt-modal-button'
        : inventoryWorksheetService.disableCompleteButton
        ? 'usf-fill-disabled-mobile-modal-button'
        : 'usf-fill-green-mobile-modal-button'"
      (click)="completeInventory()"
    >
      {{ 'i18n.inventory.inventoryWorksheet.completeInventory' | translate }}
    </ion-button>
  </div>

  <!-- Selected Item Modal -->
  <ion-modal
    [isOpen]="viewModel?.inventoryItemModalOptions?.isOpen"
    [initialBreakpoint]="platformService.platformType !== platformService.platformEnum.desktop ? 1 : undefined"
    [breakpoints]="platformService.platformType !== platformService.platformEnum.desktop  ? [0, 1] : undefined"
    (ionModalDidDismiss)="closeInventoryItemModal(false, viewModel?.inventoryItemModalOptions)"
    [ngClass]="(platformService?.isTouchExperience$ | async) ? 'ion-modal-touch' :'modal-desktop-size-xl'"
  >
    <ng-template>
      <app-inventory-item-modal
        class="ion-page"
        [inventoryItemModalOptions]="viewModel?.inventoryItemModalOptions"
        [platformType]="platformService?.platformType"
        [isAndroidTouch]="isAndroidTouch"
        (changeInventoryItemModalOptionsEmitter)="changeInventoryItemModalOptions($event)"
        (openUnitPopoverEmitter)="openUnitPopoverInItemModal($event)"
        (nonUSFNavigateEmitter)="handleNonUSFNavigate()"
        (dismissModalEmitter)="closeInventoryItemModal($event, viewModel?.inventoryItemModalOptions)"
      ></app-inventory-item-modal>
    </ng-template>
  </ion-modal>

  <ion-modal
    [isOpen]="viewModel?.inventory?.inventoryOptions?.isInfoModalOpen"
    (ionModalDidDismiss)="openOrCloseInformationModal(false)"
    [initialBreakpoint]="platformService.platformType !== platformService.platformEnum.desktop ? 1 : undefined"
    [breakpoints]="platformService.platformType !== platformService.platformEnum.desktop  ? [0, 1] : undefined"
    [ngClass]="(platformService?.isTouchExperience$ | async) ? 'ion-modal-touch' :'inventory-information-modal-desktop'"
  >
    <ng-template>
      <app-inventory-information-modal
        class="ion-page"
        [platformType]="platformService?.platformType"
        [inventoryItem]="focusedItem"
        (dismissModalEmitter)="openOrCloseInformationModal(false)"
      >
      </app-inventory-information-modal>
    </ng-template>
  </ion-modal>

  <ion-modal
    [isOpen]="viewModel?.inventory?.inventoryOptions?.isCustomUOMOpen"
    (ionModalDidDismiss)="openOrCloseCustomUOMModal(false)"
    [ngClass]="(platformService?.isTouchExperience$ | async) ? 'inventory-custom-unit-tablet' :'inventory-custom-unit-desktop'"
  >
    <ng-template>
      <app-inventory-custom-uom-modal
        class="ion-page"
        [platformType]="platformService.platformType"
        [inventoryItem]="focusedItem"
        (dismissModalEmitter)="openOrCloseCustomUOMModal(false)"
        (submitModalEmitter)="updateCustomUOMForInventoryItem($event)"
      >
      </app-inventory-custom-uom-modal>
    </ng-template>
  </ion-modal>

  <ion-modal
    [isOpen]="viewModel?.inventory?.inventoryOptions?.isSortModalOpen"
    [initialBreakpoint]="1"
    [breakpoints]="[0, 1]"
    (ionModalDidDismiss)="openOrCloseSortModal(false)"
    [ngClass]="'ion-modal-touch'"
  >
    <ng-template>
      <app-usf-sort-modal
        class="ion-page"
        [platformType]="platformService.platformType"
        [sortSelection]="sortSelection"
        [sortOptions]="viewModel?.inventory?.inventoryOptions?.inventoryWorksheetMode === InventoryWorksheetMode.count ? countModeSortOptions : editModeSortOptions"
        (saveSortEmitter)="updateInventoryWorksheetSortFromModal(
          viewModel?.inventory?.inventoryOptions?.inventoryWorksheetMode,
          viewModel?.inventory?.inventoryOptions?.selectedGroup?.groupName,
          $event
        )"
        (resetSortEmitter)="resetInventoryWorksheetSortFromModal(
          viewModel?.inventory?.inventoryOptions?.inventoryWorksheetMode,
          viewModel?.inventory?.inventoryOptions?.selectedGroup?.groupName
        )"
        (dismissModalEmitter)="openOrCloseSortModal(false)"
      >
      </app-usf-sort-modal>
    </ng-template>
  </ion-modal>
</ng-container>

<!-- New Search list modal -->
<app-new-search-simple-modal
  [isOpen]="inventoryWorksheetService?.isSearchModalOpen"
  (executeSearchEmitter)="inventoryWorksheetService?.handleSimpleSearchModalEvent($event)"
>
</app-new-search-simple-modal>
